import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const LINE_URL = 'https://lin.ee/ItfIQNV';

export const FloatingLineButton: React.FC = () => {
  const { t } = useTranslation('common');
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Show tooltip on first visit
  React.useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('line-tooltip-seen');
    if (!hasSeenTooltip) {
      setTimeout(() => {
        setShowTooltip(true);
        setTimeout(() => {
          setShowTooltip(false);
          localStorage.setItem('line-tooltip-seen', 'true');
        }, 5000);
      }, 3000);
    }
  }, []);

  return (
    <>
      {/* Tooltip */}
      {showTooltip && (
        <div 
          className="fixed bottom-24 right-6 z-50 animate-bounce"
          style={{ animation: 'bounce 1s infinite' }}
        >
          <div className="bg-white text-gray-800 px-4 py-2 rounded-lg shadow-lg text-sm font-medium relative">
            {t('support.lineTooltip')}
            <div className="absolute -bottom-2 right-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <a
        href={LINE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setShowTooltip(false)}
        aria-label={t('support.lineAriaLabel')}
      >
        {/* Pulse Animation Ring */}
        <div 
          className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-25"
          style={{ animationDuration: '2s' }}
        />
        
        {/* Button Container */}
        <div 
          className={`
            relative flex items-center justify-center
            w-14 h-14 rounded-full
            bg-[#00B900] hover:bg-[#00A000]
            shadow-lg hover:shadow-xl hover:shadow-green-500/30
            transition-all duration-300 ease-out
            ${isHovered ? 'scale-110' : 'scale-100'}
          `}
        >
          {/* LINE Icon */}
          <svg 
            viewBox="0 0 24 24" 
            className="w-8 h-8 text-white"
            fill="currentColor"
          >
            <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.349 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
          </svg>
        </div>

        {/* Hover Label */}
        <div 
          className={`
            absolute right-16 top-1/2 -translate-y-1/2
            bg-gray-900 text-white text-sm font-medium
            px-3 py-2 rounded-lg whitespace-nowrap
            transition-all duration-300
            ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'}
          `}
        >
          {t('support.lineHover')}
          <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-gray-900"></div>
        </div>
      </a>

      {/* CSS for animations */}
      <style>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </>
  );
};

export default FloatingLineButton;

